import { getIdTokenResult, sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from "firebase/auth";
import React, { useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { canAccessRoute, homePathForPermissions } from "../auth/routeAccess";
import { auth } from "../firebase";

function normalizeEmail(v: string) {
  return String(v || "").trim().toLowerCase();
}

function normalizeRole(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function normalizePermissionLevel(value: unknown) {
  return value === "manage" || value === "edit" || value === "view" ? value : "none";
}

function hasAdminWebAccess(claims: Record<string, unknown>) {
  const role = normalizeRole(claims.role);
  if (
    claims.superAdmin === true ||
    claims.admin === true ||
    claims.office === true ||
    claims.teacher === true ||
    claims.developer === true ||
    claims.appDeveloper === true ||
    claims.webDeveloper === true
  ) {
    return true;
  }

  if (["superadmin", "admin", "office", "teacher", "developer", "appdeveloper", "webdeveloper"].includes(role)) {
    return true;
  }

  const permissions = claims.p;
  if (permissions && typeof permissions === "object") {
    return Object.values(permissions as Record<string, unknown>).some((value) => normalizePermissionLevel(value) !== "none");
  }

  return false;
}

export function LoginPage() {
  const loc = useLocation();
  const { user, loading, hasPermission } = useAuth();
  const qs = new URLSearchParams(loc.search);

  const returnTo = useMemo(() => {
    const next = qs.get("returnTo") || "/";
    return next.startsWith("/login") ? "/" : next;
  }, [loc.search]);
  const reason = useMemo(() => qs.get("reason") || "", [loc.search]);
  const emailParam = useMemo(() => qs.get("email") || "", [loc.search]);

  const [email, setEmail] = useState(emailParam);
  const [pass, setPass] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const emailNorm = normalizeEmail(email);

  if (loading) {
    return (
      <div className="center">
        <div className="card" style={{ maxWidth: 520 }}>
          <h2>ZCA Admin Sign In</h2>
          <p className="muted">Checking your session...</p>
        </div>
      </div>
    );
  }

  if (user) {
    const landingPath = canAccessRoute(returnTo, hasPermission) ? returnTo : homePathForPermissions(hasPermission);
    return <Navigate to={landingPath} replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setMessage(null);
    setBusy(true);

    try {
      const credential = await signInWithEmailAndPassword(auth, emailNorm, pass);
      const token = await getIdTokenResult(credential.user, true);
      if (!hasAdminWebAccess((token?.claims || {}) as Record<string, unknown>)) {
        await signOut(auth);
        throw new Error(
          "This account is not configured for admin-web access. Parent and family accounts should use the family app or parent portal instead."
        );
      }
    } catch (e: any) {
      setErr(e?.message || "Sign in failed.");
    } finally {
      setBusy(false);
    }
  }

  async function onForgotPassword() {
    setErr(null);
    setMessage(null);

    if (!emailNorm) {
      setErr("Enter your email address first, then choose Forgot password.");
      return;
    }

    setResetBusy(true);
    try {
      await sendPasswordResetEmail(auth, emailNorm, {
        url: `${window.location.origin}/login?email=${encodeURIComponent(emailNorm)}`,
        handleCodeInApp: false,
      });
      setMessage("Password reset email sent. Check your inbox and spam folder, then return here to sign in.");
    } catch (e: any) {
      setErr(e?.message || "Failed to send password reset email.");
    } finally {
      setResetBusy(false);
    }
  }

  return (
    <div className="center">
      <div className="card" style={{ maxWidth: 520 }}>
        <h2>ZCA Admin Sign In</h2>
        <p className="muted">Use your school account to access the admin web.</p>

        {reason === "idle" ? (
          <div className="muted" style={{ marginBottom: 12 }}>
            Your session ended due to inactivity. Please sign in again.
          </div>
        ) : null}

        {err ? <div className="error">{err}</div> : null}
        {message ? <div className="notice-light">{message}</div> : null}

        <form onSubmit={onSubmit} className="form">
          <label className="label">Email</label>
          <input
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />

          <label className="label">Password</label>
          <input
            className="input"
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
          />

          <button className="btn" disabled={busy || !emailNorm || !pass}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button className="btn secondary" type="button" disabled={resetBusy || !emailNorm} onClick={onForgotPassword}>
            {resetBusy ? "Sending reset..." : "Forgot password"}
          </button>
        </form>
        <p className="muted" style={{ marginTop: 14 }}>
          Parent/basic accounts use the family app or parent portal. Admin-web is only for staff, office, admin, and approved operational users.
        </p>
      </div>
    </div>
  );
}
